// /utils/printExpTemplate.js

/**
 * Format date values to DD/MM/YYYY
 * @param {string|Date} dateValue - Date value to format
 * @returns {string} - Formatted date string or empty string if invalid
 */
function formatDate(dateValue) {
  if (!dateValue) return '';
  
  try {
    const date = new Date(dateValue);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return '';
    
    // Format as DD/MM/YYYY
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Date formatting error:', error);
    return '';
  }
}

/**
 * Format a variable value to display nicely (handling null, undefined, etc.)
 * @param {any} value - The variable value to format
 * @returns {string} - Formatted value as string
 */
function formatVariableValue(value) {
  // Return empty string for null or undefined
  if (value === null || value === undefined) {
    return '';
  }
  
  // Convert to string
  const strValue = String(value);
  
  // Return empty string for "null" or "undefined" literal strings or empty strings
  if (strValue === 'null' || strValue === 'undefined' || strValue.trim() === '') {
    return '';
  }
  
  return strValue;
}

/**
 * Process template text and replace variables and conditions
 * @param {string} text - Template text with variables and conditions
 * @param {Object} variables - The variables object for substitution
 * @returns {string} - Processed text with variables replaced and conditions evaluated
 */
// Fix for printTemplate.js
// Replace the processTemplateText function with this version:

function processTemplateText(text, variables) {
  if (!text) return '';
  
  // First handle null and !null conditional logic patterns
  let processedText = text.replace(/\{\{if:([^:]+?)(null|!null):([^}]+)\}\}/g, 
    (match, variable, operator, result) => {
      const trimmedVariable = variable.trim();
      const trimmedOperator = operator.trim();
      const trimmedResult = result.trim();
      
      // Get the actual variable value
      const actualValue = variables[trimmedVariable] !== undefined 
        ? formatVariableValue(variables[trimmedVariable]) 
        : '';
      
      // Determine if condition is satisfied
      let conditionMet = false;
      
      // Handle special null check cases
      if (trimmedOperator === 'null') {
        // Check if the value is null, undefined, empty string, or just whitespace
        conditionMet = !actualValue || actualValue.trim() === '';
      } else if (trimmedOperator === '!null') {
        // Check if the value exists and is not just whitespace
        conditionMet = actualValue && actualValue.trim() !== '';
      }
      
      return conditionMet ? trimmedResult : '';
    });
  
  // Then handle regular conditional logic with operators
  processedText = processedText.replace(/\{\{if:([^:=!<>]+)([=!<>]+)([^:]*):([^}]+)\}\}/g, 
    (match, variable, operator, value, result) => {
      const trimmedVariable = variable.trim();
      const trimmedOperator = operator.trim();
      const trimmedValue = value ? value.trim() : '';
      const trimmedResult = result.trim();
      
      // Get the actual variable value
      const actualValue = variables[trimmedVariable] !== undefined 
        ? formatVariableValue(variables[trimmedVariable]) 
        : '';
      
      // Determine if condition is satisfied
      let conditionMet = false;
      
      // Handle regular comparison operators
      switch (trimmedOperator) {
        case '=':
        case '==':
          conditionMet = actualValue === trimmedValue;
          break;
        case '!=':
          conditionMet = actualValue !== trimmedValue;
          break;
        case '>':
          conditionMet = Number(actualValue) > Number(trimmedValue);
          break;
        case '<':
          conditionMet = Number(actualValue) < Number(trimmedValue);
          break;
        case '>=':
          conditionMet = Number(actualValue) >= Number(trimmedValue);
          break;
        case '<=':
          conditionMet = Number(actualValue) <= Number(trimmedValue);
          break;
        default:
          conditionMet = actualValue === trimmedValue;
      }
      
      return conditionMet ? trimmedResult : '';
    });
  
  // Finally handle regular variable placeholders
  processedText = processedText.replace(/\{\{([^}]+)\}\}/g, (match, varName) => {
    const trimmedName = varName.trim();
    
    if (variables[trimmedName] === undefined) {
      return ''; // Replace with empty string if variable not found
    }
    
    // Return the formatted variable value (handling null/undefined)
    return formatVariableValue(variables[trimmedName]);
  });
  
  return processedText;
}

// Fix for printExpPreview.js
// Replace the processTemplateText function with the exact same implementation as above.
// The implementation is identical for both files.

/**
 * Generates a printable PDF from a template with variable substitution without showing any window
 * @param {Array} templateJSON - The template JSON array with element definitions
 * @param {Object} variablesJSON - The variables object for substitution
 * @param {Object} options - Additional options (optional)
 * @param {boolean} options.closeAfterPrint - Whether to remove the iframe after printing (default: true)
 * @returns {HTMLIFrameElement} - The iframe element used for printing
 */
function printTemplate(templateJSON, variablesJSON, options = {}) {
  // Default options
  const defaultOptions = {
    closeAfterPrint: true
  };
  const printOptions = { ...defaultOptions, ...options };
  
  // Validate input parameters
  if (!Array.isArray(templateJSON)) {
    throw new Error('Template JSON must be an array');
  }
  
  if (!variablesJSON || typeof variablesJSON !== 'object') {
    throw new Error('Variables JSON must be an object');
  }
  
  // Make a deep copy to avoid modifying the original object
  const processedVariables = JSON.parse(JSON.stringify(variablesJSON));
  
  // 1. Clean up all null values to ensure they're all empty strings, not "null" text
  Object.keys(processedVariables).forEach(key => {
    if (processedVariables[key] === null || processedVariables[key] === undefined) {
      processedVariables[key] = '';
    }
  });
  
  // 2. Apply special logic: if ville_dest is not null, set ADRESSE and VILLE to null
  if (processedVariables.ville_dest && 
      String(processedVariables.ville_dest).trim() !== '') {
    // When ville_dest has a value, set ADRESSE and VILLE to empty strings
    processedVariables.ADRESSE = '';
    processedVariables.VILLE = '';
  }
  
  // 3. Format date fields to DD/MM/YYYY
  const dateFields = [
    'date_exp', 'date_fac', 'date_reg', 'date_recep', 
    'date_dev', 'der_maj'
  ];
  
  dateFields.forEach(field => {
    if (processedVariables[field]) {
      processedVariables[field] = formatDate(processedVariables[field]);
    }
  });
  
  // Create an invisible iframe in the current document
  const printFrame = document.createElement('iframe');
  
  // Make it invisible
  printFrame.style.position = 'fixed';
  printFrame.style.width = '0';
  printFrame.style.height = '0';
  printFrame.style.border = '0';
  printFrame.style.opacity = '0';
  printFrame.style.pointerEvents = 'none';
  
  // Add it to the document
  document.body.appendChild(printFrame);
  
  // Get the iframe's document
  const frameDoc = printFrame.contentDocument || printFrame.contentWindow.document;
  
  // Write the content to the iframe
  frameDoc.open();
  frameDoc.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Template Print</title>
      <style>
        /* These settings force landscape mode */
        @page {
          size: A4 landscape;  /* Use A4 paper in landscape orientation */
          margin: 0;
        }
        
        html, body {
          margin: 0;
          padding: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          font-family: Arial, sans-serif;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .canvas-container { 
          width: 100%;
          height: 100%;
          position: relative; 
          background-color: white;
        }
        
        .element {
          position: absolute;
          /* No borders in the printed version */
        }
        
        .element-text {
          font-weight: bold;
        }
        
        @media print {
          /* This reinforces landscape printing */
          @page {
            size: landscape;
          }
          
          body {
            width: 297mm;  /* A4 width in landscape */
            height: 210mm; /* A4 height in landscape */
          }
        }
      </style>
    </head>
    <body>
      <div class="canvas-container">
        ${templateJSON.map(element => `
          <div class="element" style="
            left: ${element.x}px; 
            top: ${element.y}px; 
            width: ${element.width}px; 
            height: ${element.height}px;
            color: ${element.textColor || '#000'};"
          >
            <div class="element-text" style="font-size: ${element.fontSize || 16}px;">
              ${processTemplateText(element.text || element.id, processedVariables)}
            </div>
          </div>
        `).join('')}
      </div>
    </body>
    </html>
  `);
  frameDoc.close();
  
  // Function to handle the print operation
  const handlePrint = () => {
    try {
      // Wait for content to be fully loaded
      if (printFrame.contentWindow.document.readyState !== 'complete') {
        setTimeout(handlePrint, 50);
        return;
      }
      
      // Execute print
      printFrame.contentWindow.print();
      
      // Remove iframe after printing if specified
      if (printOptions.closeAfterPrint) {
        setTimeout(() => {
          document.body.removeChild(printFrame);
        }, 1000);
      }
    } catch (error) {
      console.error('Print error:', error);
      // Clean up on error
      document.body.removeChild(printFrame);
    }
  };
  
  // Start the print process
  setTimeout(handlePrint, 100);
  
  // Return the iframe in case further manipulation is needed
  return printFrame;
}

/**
 * Usage examples:
 * 
 * // Basic usage with template and variables (direct printing)
 * printTemplate(savedTemplateJSON, { customer_name: "Jane Smith", invoice_number: "INV-2023-005" });
 * 
 * // Example with conditional logic
 * printTemplate(savedTemplateJSON, { 
 *   customer_name: "John Doe", 
 *   total_amount: 2450.75,
 *   payment_method: "CHQ",
 *   port_p: "PP"
 * });
 * 
 * // Example with null check conditions
 * printTemplate(savedTemplateJSON, {
 *   customer_name: "",  // Empty string will trigger null conditions
 *   notes: null,        // Null will trigger null conditions and display as empty string
 *   payment_method: "CHQ",
 *   some_field: undefined  // Undefined will also display as empty string
 * });
 * 
 * // Example with date formatting
 * printTemplate(savedTemplateJSON, {
 *   date_exp: "2023-05-15T14:30:00", // Will be displayed as 15/05/2023
 *   date_fac: new Date()            // Today's date formatted as DD/MM/YYYY
 * });
 * 
 * // Example with ville_dest special logic
 * printTemplate(savedTemplateJSON, {
 *   ville_dest: "Paris",  // When ville_dest has a value, ADRESSE and VILLE will be set to empty
 *   ADRESSE: "123 rue de la Paix", // This will be ignored when ville_dest has a value
 *   VILLE: "Lyon" // This will be ignored when ville_dest has a value
 * });
 * 
 * // Keep the iframe in the DOM after printing (if you plan to reuse it)
 * const printFrame = printTemplate(
 *   savedTemplateJSON, 
 *   { customer_name: "John Doe", total_amount: "$2,450.75" },
 *   { closeAfterPrint: false }
 * );
 * 
 * // Later you can manually remove it if needed
 * // document.body.removeChild(printFrame);
 */

export default printTemplate;